// Página pública de Posiciones de Grupos del Mundial 2026
import { useQuery } from '@tanstack/react-query'
import { motion } from 'framer-motion'
import { Trophy, HelpCircle, ShieldAlert } from 'lucide-react'
import client from '../api/client'
import Card from '../components/ui/Card'
import Flag from '../components/ui/Flag'
import Skeleton from '../components/ui/Skeleton'
import AdCarousel from '../components/ui/AdCarousel'

export default function WorldCupStandings() {
  // Obtener los grupos y sus partidos de la API
  const { data: groups = [], isLoading, error } = useQuery({
    queryKey: ['publicGroups'],
    queryFn: async () => {
      const response = await client.get('/groups')
      return response.data?.datos || []
    }
  })

  // Obtener publicidades desde la API
  const { data: ads = [] } = useQuery({
    queryKey: ['activeAds-internas'],
    queryFn: async () => {
      const response = await client.get('/ads?ubicacion=internas')
      return response.data?.datos || []
    }
  })

  // Función para calcular la tabla de posiciones dinámica para un grupo
  const calculateStandings = (equipos = [], partidos = []) => {
    // Inicializar estadísticas de los equipos
    const teamStats = equipos.map(eq => ({
      id: eq.id,
      nombre: eq.nombre,
      bandera: eq.bandera,
      pj: 0,
      pg: 0,
      pe: 0,
      pp: 0,
      gf: 0,
      gc: 0,
      dg: 0,
      pts: 0
    }))

    // Procesar los partidos finalizados
    partidos.forEach(p => {
      if (p.estado === 'finalizado' && p.goles_local !== null && p.goles_visitante !== null) {
        const local = teamStats.find(t => t.id === p.equipo_local_id)
        const visitante = teamStats.find(t => t.id === p.equipo_visitante_id)

        if (local && visitante) {
          const gl = parseInt(p.goles_local)
          const gv = parseInt(p.goles_visitante)

          local.pj += 1
          visitante.pj += 1
          local.gf += gl
          local.gc += gv
          visitante.gf += gv
          visitante.gc += gl

          if (gl > gv) {
            local.pg += 1
            local.pts += 3
            visitante.pp += 1
          } else if (gv > gl) {
            visitante.pg += 1
            visitante.pts += 3
            local.pp += 1
          } else {
            local.pe += 1
            visitante.pe += 1
            local.pts += 1
            visitante.pts += 1
          }
        }
      }
    })

    // Calcular la diferencia de goles
    teamStats.forEach(t => {
      t.dg = t.gf - t.gc
    })

    // Ordenar según las reglas oficiales de la FIFA:
    // 1. Puntos
    // 2. Diferencia de goles
    // 3. Goles a favor
    // 4. Orden alfabético (desempate base)
    return teamStats.sort((a, b) => {
      if (b.pts !== a.pts) return b.pts - a.pts
      if (b.dg !== a.dg) return b.dg - a.dg
      if (b.gf !== a.gf) return b.gf - a.gf
      return a.nombre.localeCompare(b.nombre)
    })
  }

  if (isLoading) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto space-y-6">
        <Skeleton className="h-12 w-64 rounded-xl" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(6)].map((_, i) => (
            <Skeleton key={i} className="h-96 rounded-2xl" />
          ))}
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto text-center py-20">
        <div className="w-16 h-16 bg-error/10 text-error rounded-2xl flex items-center justify-center mx-auto mb-4 border border-error/20">
          <ShieldAlert className="w-8 h-8" />
        </div>
        <h2 className="text-xl font-display font-bold text-text-primary mb-2">Error al cargar la clasificación</h2>
        <p className="text-sm text-text-secondary">No pudimos conectar con el servidor para obtener los datos de los grupos.</p>
      </div>
    )
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto space-y-4">
      {/* Banner Publicitario */}
      <AdCarousel ads={ads} variant="banner-only" />

      {/* Encabezado */}
      <div className="mb-8">
        <h1 className="text-3xl sm:text-4xl font-display font-bold text-text-primary">
          Tablas de Clasificación 📊
        </h1>
        <p className="text-text-secondary mt-1.5">
          Posiciones en tiempo real de los 12 grupos del Mundial 2026. Los dos primeros lugares de cada grupo clasifican automáticamente a los dieciseisavos de final.
        </p>
      </div>

      {/* Grid de Grupos */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {groups.map((group, index) => {
          const standings = calculateStandings(group.equipos, group.partidos)

          return (
            <motion.div
              key={group.id}
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.05 }}
            >
              <Card className="h-full flex flex-col justify-between border border-border-subtle bg-surface-card/45 relative overflow-hidden group">
                {/* Cabecera Grupo */}
                <div className="px-5 py-4 border-b border-border-subtle bg-navy-dark/30 flex items-center justify-between">
                  <h3 className="font-display font-bold text-text-primary text-base flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full bg-cyan" />
                    Grupo {group.nombre}
                  </h3>
                  <span className="text-[10px] uppercase font-bold tracking-widest text-cyan font-display">
                    Fase de Grupos
                  </span>
                </div>

                {/* Tabla de Posiciones */}
                <div className="flex-1 overflow-x-auto">
                  <table className="w-full text-left border-collapse text-xs">
                    <thead>
                      <tr className="border-b border-border-subtle text-[10px] text-text-muted font-bold font-display uppercase tracking-wider bg-navy-dark/10">
                        <th className="py-3 px-3 text-center w-8">#</th>
                        <th className="py-3 px-2">Selección</th>
                        <th className="py-3 px-2 text-center w-8">PJ</th>
                        <th className="py-3 px-2 text-center w-8">G</th>
                        <th className="py-3 px-2 text-center w-8">E</th>
                        <th className="py-3 px-2 text-center w-8">P</th>
                        <th className="py-3 px-2 text-center w-8">GF</th>
                        <th className="py-3 px-2 text-center w-8">GC</th>
                        <th className="py-3 px-2 text-center w-8">DG</th>
                        <th className="py-3 px-3 text-center w-10 bg-navy-dark/30">Pts</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border-subtle/40">
                      {standings.map((team, idx) => {
                        const isQualifyingZone = idx < 2 // Los 2 primeros pasan

                        return (
                          <tr
                            key={team.id}
                            className={`transition-colors font-body ${
                              isQualifyingZone
                                ? 'bg-success/[0.02] hover:bg-success/[0.04]'
                                : 'hover:bg-surface-card-hover/20'
                            }`}
                          >
                            {/* Posición */}
                            <td className="py-3.5 px-3 text-center">
                              <span
                                className={`w-5 h-5 inline-flex items-center justify-center rounded-full font-bold text-[10px] ${
                                  isQualifyingZone
                                    ? 'bg-success/20 text-success border border-success/30'
                                    : 'bg-navy-dark/40 text-text-muted border border-border-subtle'
                                }`}
                              >
                                {idx + 1}
                              </span>
                            </td>

                            {/* Equipo */}
                            <td className="py-3.5 px-2 font-semibold text-text-primary whitespace-nowrap">
                              <div className="flex items-center gap-2">
                                <Flag code={team.bandera} className="w-5.5 h-3.5 object-cover rounded shadow-sm shrink-0" />
                                <span className="truncate max-w-[85px] sm:max-w-[100px]">{team.nombre}</span>
                              </div>
                            </td>

                            {/* PJ, PG, PE, PP */}
                            <td className="py-3.5 px-2 text-center text-text-secondary">{team.pj}</td>
                            <td className="py-3.5 px-2 text-center text-text-secondary">{team.pg}</td>
                            <td className="py-3.5 px-2 text-center text-text-secondary">{team.pe}</td>
                            <td className="py-3.5 px-2 text-center text-text-secondary">{team.pp}</td>

                            {/* GF, GC, DG */}
                            <td className="py-3.5 px-2 text-center text-text-muted">{team.gf}</td>
                            <td className="py-3.5 px-2 text-center text-text-muted">{team.gc}</td>
                            <td className={`py-3.5 px-2 text-center font-semibold font-mono ${
                              team.dg > 0 ? 'text-success' : team.dg < 0 ? 'text-error' : 'text-text-muted'
                            }`}>
                              {team.dg > 0 ? `+${team.dg}` : team.dg}
                            </td>

                            {/* Puntos */}
                            <td className={`py-3.5 px-3 text-center font-bold text-sm bg-navy-dark/20 ${
                              isQualifyingZone ? 'text-success' : 'text-text-secondary'
                            }`}>
                              {team.pts}
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Leyenda de pase */}
                <div className="px-5 py-2.5 border-t border-border-subtle bg-navy-dark/10 flex items-center justify-between text-[10px] text-text-muted font-body">
                  <div className="flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full bg-success" />
                    <span>Zona de Clasificación</span>
                  </div>
                  <Trophy className="w-3.5 h-3.5 text-cyan shrink-0" />
                </div>
              </Card>
            </motion.div>
          )
        })}
      </div>
    </div>
  )
}
